import React from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Navbar, Footer } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import {
  ArrowRight,
  Quote,
  FileText,
  Shield,
  Zap,
  RefreshCw,
  Building2,
  Cpu,
  Landmark,
} from 'lucide-react';

const CATEGORIES = [
  {
    icon: FileText,
    title: 'Mandate',
    q: 'What did the customer authorize the agent to do?',
    who: 'You + Kojiru',
    kojiru: 'KYPA agents post their pledge and their promises publicly at registration. Your platform inherits the mandate contract, on-chain, indexable.',
    accent: 'slate',
  },
  {
    icon: Shield,
    title: 'Evidence',
    q: 'Can you reconstruct the transaction end to end?',
    who: 'You + Kojiru',
    kojiru: 'Every KYPA-attested transaction produces a signed, deterministic record on Base. Replayable forever. Auditor-friendly. Zero manual reconstruction.',
    accent: 'slate',
  },
  {
    icon: Zap,
    title: 'Recourse',
    q: 'Who makes the harmed party whole?',
    who: 'Kojiru',
    kojiru: 'The breach is recorded publicly against the operator&rsquo;s pledge, a public commitment signal. Remedies stay with the parties and existing dispute processes. No custody. Not insurance.',
    accent: 'rose',
    ours: true,
  },
  {
    icon: RefreshCw,
    title: 'Reconciliation',
    q: 'Does settlement resolve without a person remembering what happened?',
    who: 'You + Kojiru',
    kojiru: 'Each breach proof produces a public on-chain record. Your ledger reconciles against the on-chain event. No end-of-day exception queue.',
    accent: 'slate',
  },
];

const USE_CASES = [
  {
    icon: Building2,
    title: 'PayFacs',
    body: 'Embed Kojiru\u2019s KYPA in your sub-merchant onboarding. Every agent transacting through your platform inherits a public pledge and breach record without your engineers writing the hard part.',
  },
  {
    icon: Cpu,
    title: 'Vertical processors',
    body: 'Add a "KYPA-verified" filter to your merchant search or your agent marketplace. Buyers see the pledge; disputes resolve on-chain; your dispute team shrinks.',
  },
  {
    icon: Landmark,
    title: 'ISOs & acquirers',
    body: 'Route agentic transactions through KYPA-attested rails. Chargebacks that would have hit your reserve now hit the offending agent\u2019s pledge instead.',
  },
];

export default function ForPaymentsPlatforms() {
  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white">
      <Helmet>
        <title>Kojiru for Payments Platforms · Recourse, Embedded</title>
        <meta
          name="description"
          content="For PayFacs, ISOs, processors, and vertical payment platforms. Add the recourse layer to your agentic-commerce readiness in weeks, not quarters."
        />
        <meta property="og:title" content="Kojiru for Payments Platforms" />
      </Helmet>

      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-16 px-6 border-b border-white/5">
        <div className="max-w-4xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="text-xs font-bold tracking-[2px] text-amber-400 mb-6">
              FOR PAYMENTS PLATFORMS
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05] tracking-tight mb-6">
              Not in the demo. In the dispute.
            </h1>
            <p className="text-lg text-slate-300 leading-relaxed max-w-3xl mb-4">
              If you run a PayFac, an ISO, a vertical processor, or any payment platform, the next
              agentic-commerce standards will be written by the companies inside the Alliance. The
              losses will be absorbed by the companies that are not ready. Being ready is not about a
              seat at the table: it is about transaction state you can trust, evidence you can
              produce on demand, and exception paths that do not depend on a person remembering what
              happened.
            </p>
            <p className="text-sm text-slate-500 italic">
              Framing: a senior payments-industry analyst (25+ years), <em>Inside Payments</em>, 22 August 2026.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Direct-to-agent distribution ribbon */}
      <section className="py-8 px-6 border-b border-white/5" data-testid="platforms-direct-distribution-ribbon">
        <div className="max-w-4xl mx-auto">
          <div className="border border-cyan-500/30 rounded-2xl bg-cyan-500/5 p-6 md:p-8">
            <div className="text-[10px] font-bold tracking-[2.4px] text-cyan-300 mb-3">
              THE +60&ndash;72% DIRECT-TO-AGENT REVENUE MECHANIC &middot; SEPTEMBER 2026
            </div>
            <p className="text-sm text-slate-300 leading-relaxed mb-3">
              An independent senior payments-industry analyst modeled the two largest US card schemes capturing roughly <strong className="text-white">+60&ndash;72% incremental gross revenue</strong> by distributing tokenized credentials directly to LLMs, personal agents, and digital wallets: a greenfield channel that bypasses issuing banks and core processors entirely. The projected mechanic is <em className="not-italic text-white">governed account access</em>: charging for provisioning, lifecycle, credential refresh, authentication, and agent controls at the token layer, not just at checkout.
            </p>
            <p className="text-sm text-slate-300 leading-relaxed">
              KYPA verify sits underneath that mechanic. Every tokenized credential the schemes distribute directly to an agent still needs the same three answers a merchant, acquirer, or platform will ask: <em className="not-italic text-white">is the mandate scope intact, is there pledge behind the credential, and does recourse resolve without an arbiter?</em> Kojiru is the recourse layer under whichever tokenization service ships: scheme-neutral, non-custodial, MIT-licensed, Base L2 mainnet.
            </p>
            <div className="text-[10px] text-slate-500 mt-3 tracking-[1.4px]">
              SOURCE: INDEPENDENT SENIOR PAYMENTS-INDUSTRY ANALYST (25+ YEARS), LINKEDIN COMMENTARY, 23 SEPTEMBER 2026 &middot; INSTITUTIONAL DESCRIPTOR ONLY PER NAMING POLICY
            </div>
          </div>
        </div>
      </section>

      {/* Four categories */}
      <section className="py-20 px-6 border-b border-white/5">
        <div className="max-w-6xl mx-auto">
          <div className="text-xs font-bold tracking-[2px] text-cyan-400 mb-3">FOUR CATEGORIES</div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black mb-4 leading-tight">
            Mandate. Evidence. Recourse. Reconciliation.
          </h2>
          <p className="text-slate-400 max-w-3xl mb-10">
            Every platform preparing for agentic commerce must solve these four. Kojiru handles the third
            outright, and gives you native primitives for the other three.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {CATEGORIES.map((c) => (
              <motion.div
                key={c.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className={`rounded-xl p-6 border ${
                  c.ours
                    ? 'bg-gradient-to-br from-rose-500/10 to-rose-500/[0.02] border-rose-500/40 shadow-[0_0_40px_-15px_rgba(244,63,94,0.5)]'
                    : 'bg-[#0f0f16] border-white/10'
                }`}
                data-testid={`payments-category-${c.title.toLowerCase()}`}
              >
                <c.icon className={`w-6 h-6 mb-4 ${c.ours ? 'text-rose-400' : 'text-slate-500'}`} />
                <h3 className={`text-2xl font-black mb-2 ${c.ours ? 'text-white' : 'text-white'}`}>{c.title}</h3>
                <p className="text-sm text-slate-400 italic mb-4">&ldquo;{c.q}&rdquo;</p>
                <div className={`text-[10px] font-bold tracking-[1.5px] mb-2 ${c.ours ? 'text-rose-300' : 'text-slate-500'}`}>
                  KOJIRU&rsquo;S ROLE &middot; {c.who}
                </div>
                <p className="text-sm text-slate-300 leading-relaxed" dangerouslySetInnerHTML={{ __html: c.kojiru }} />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* EU AI Act box */}
      <section className="py-14 px-6 border-b border-white/5 bg-white/[0.02]">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-lg bg-blue-500/20 border border-blue-500/40 flex-shrink-0 flex items-center justify-center">
              <Landmark className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <div className="text-[10px] font-bold tracking-[2px] text-blue-300 mb-1">EU AI ACT &middot; ARTICLE 15</div>
              <h3 className="text-xl font-bold text-white mb-3">Audit-trail alignment out of the box.</h3>
              <p className="text-sm text-slate-300 leading-relaxed">
                The EU AI Act&rsquo;s high-risk obligations took full effect this month. Article 15 requires
                that automated systems be auditable end-to-end with non-repudiable records. KYPA-attested
                transactions produce exactly that class of evidence: on Base, permissionless,
                deterministic, exportable to any auditor in under a minute.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Use cases */}
      <section className="py-20 px-6 border-b border-white/5">
        <div className="max-w-6xl mx-auto">
          <div className="text-xs font-bold tracking-[2px] text-cyan-400 mb-3">WHO USES IT</div>
          <h2 className="text-3xl sm:text-4xl font-black mb-10 leading-tight">
            Three platform shapes. One integration.
          </h2>
          <div className="grid md:grid-cols-3 gap-4">
            {USE_CASES.map((u) => (
              <div key={u.title} className="bg-[#0f0f16] border border-white/10 rounded-xl p-6">
                <u.icon className="w-6 h-6 text-cyan-400 mb-4" />
                <h3 className="text-lg font-bold text-white mb-3">{u.title}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{u.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Close */}
      <section className="py-20 px-6 bg-gradient-to-b from-transparent to-amber-950/10">
        <div className="max-w-4xl mx-auto text-center">
          <div className="flex justify-center mb-6">
            <Quote className="w-8 h-8 text-amber-400" />
          </div>
          <p className="text-2xl sm:text-3xl italic text-white leading-snug max-w-3xl mx-auto mb-3">
            &ldquo;The standards will be written by the companies in the room. The losses will be
            absorbed by the companies that are not ready.&rdquo;
          </p>
          <p className="text-sm text-slate-500 mb-10">
            Senior payments-industry analyst (25+ years) &middot; <em>Inside Payments</em> &middot; 22 August 2026
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="mailto:wayne@kojiru.com?subject=KYPA%20for%20Payments%20Platforms%20%E2%80%94%2030-minute%20walkthrough">
              <Button size="lg" className="bg-white text-black hover:bg-slate-100" data-testid="cta-walkthrough">
                Get a 30-minute walkthrough
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </a>
            <Link to="/three-analysts-one-gap">
              <Button size="lg" variant="outline" className="border-white/20 text-white hover:bg-white/5" data-testid="cta-three-analysts">
                Read the essay
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
